import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { nativeHaptic } from "../utils/native_bridge"

// Keyboard + modal triage for the unified Messages list. List mode: arrows
// (or j/k) move a visual selection, Space/Enter previews, ArrowRight
// archives, ArrowLeft queues a draft reply. Triage mode (preview modal open):
// the same left/right keys act on the previewed email and auto-advance to the
// next one. Inside the modal the card drags Tinder-style and flies off before
// the same action fires. List-row swipes live in row-swipe, not here. Attach
// alongside email-modal on the list.
export default class extends Controller {
    static values = { draftStatusUrl: String }

    SWIPE_CLAIM_PX = 24
    MODAL_THRESHOLD_RATIO = 0.3
    MODAL_FLICK_VELOCITY = 0.5 // px/ms
    MODAL_ROTATE_DEG_PER_PX = 0.05
    DRAFT_POLL_MS = 3000
    DRAFT_TIMEOUT_MS = 120000

    // Handlers are bound once per instance so a repeated connect() (Turbo
    // morph/preview quirks) re-adds the same references, which the browser
    // dedupes instead of stacking duplicate listeners.
    initialize() {
        this._onKeydown = this._handleKeydown.bind(this)
        this._onOpened = (e) => {
            this._advancing = false
            let trigger = e.detail && e.detail.trigger
            let row = trigger && trigger.closest ? trigger.closest(".assistant-thread-item[data-message-id]") : null
            if (row && this.element.contains(row)) {
                this._modalRow = row
                this._select(row)
            }
            this._bindModalSwipe()
            if (this._modalRow) this._ensureActionBar()
        }
        this._onClosed = () => {
            if (this._advancing) return
            this._modalRow = null
        }
    }

    connect() {
        this._selected = null
        this._modalRow = null
        this._advancing = false
        this._pendingDrafts = new Map() // message id -> enqueue timestamp
        this._pollTimer = null

        document.addEventListener("keydown", this._onKeydown)
        this.element.addEventListener("email-modal:opened", this._onOpened)
        this.element.addEventListener("email-modal:closed", this._onClosed)
    }

    disconnect() {
        document.removeEventListener("keydown", this._onKeydown)
        this.element.removeEventListener("email-modal:opened", this._onOpened)
        this.element.removeEventListener("email-modal:closed", this._onClosed)
        if (this._pollTimer) clearTimeout(this._pollTimer)
    }

    // ----- Keyboard -----

    _handleKeydown(e) {
        if (e.metaKey || e.ctrlKey || e.altKey) return
        let t = e.target
        if (t && t.matches && (t.matches("input, textarea, select") || t.isContentEditable)) return

        // The integrated card stack owns the keyboard while it's open.
        let stack = this._stackController()
        if (stack && stack.isOpen()) return

        if (this._modalOpen() && this._modalRow) {
            this._handleModalKey(e)
            return
        }

        let key = e.key
        if (key === "ArrowDown" || key === "j") {
            e.preventDefault()
            this._move(1)
        } else if (key === "ArrowUp" || key === "k") {
            e.preventDefault()
            this._move(-1)
        } else if (key === " " && this._selected) {
            if (this._isEmail(this._selected)) {
                e.preventDefault()
                this._openPreview(this._selected)
            }
        } else if (key === "Enter" && this._selected) {
            e.preventDefault()
            this._selected.click()
        } else if (key === "ArrowRight" && this._selected && this._isEmail(this._selected)) {
            e.preventDefault()
            this._archive(this._selected)
        } else if (key === "ArrowLeft" && this._selected && this._isEmail(this._selected)) {
            e.preventDefault()
            let row = this._selected
            this._draft(row)
            // The drafted row stays in the list ("Drafting…" badge), but the
            // user is done with it — move the highlight along.
            this._advanceSelectionFrom(row)
        }
    }

    _handleModalKey(e) {
        let key = e.key
        if (key === "ArrowRight") {
            e.preventDefault()
            this._archive(this._modalRow, { advance: true })
        } else if (key === "ArrowLeft") {
            e.preventDefault()
            this._draft(this._modalRow, { advance: true })
        } else if (key === "ArrowDown" || key === "j") {
            e.preventDefault()
            this._previewSibling(1)
        } else if (key === "ArrowUp" || key === "k") {
            e.preventDefault()
            this._previewSibling(-1)
        }
    }

    // ----- Selection -----

    _rows() {
        return Array.from(this.element.querySelectorAll(".assistant-thread-item")).filter((r) => !r.closest(".is-leaving"))
    }

    _emailRows() {
        return this._rows().filter((r) => this._isEmail(r))
    }

    _isEmail(row) {
        return !!row.dataset.messageId
    }

    _move(dir) {
        let rows = this._rows()
        if (!rows.length) return
        let idx = rows.indexOf(this._selected)
        let next = idx === -1 ? (dir > 0 ? 0 : rows.length - 1) : Math.min(Math.max(idx + dir, 0), rows.length - 1)
        this._select(rows[next])
    }

    _select(row) {
        if (this._selected && this._selected !== row) this._selected.classList.remove("is-selected")
        this._selected = row
        if (row) {
            row.classList.add("is-selected")
            row.scrollIntoView({ block: "nearest" })
        }
    }

    // ----- Preview modal / triage mode -----

    _modalController() {
        return this.application.getControllerForElementAndIdentifier(this.element, "email-modal")
    }

    // The Messages list mounts card-stack instead of email-modal; previews
    // route there and the modal-mode code below only runs in contexts that
    // still pair this controller with email-modal.
    _stackController() {
        return this.application.getControllerForElementAndIdentifier(this.element, "card-stack")
    }

    _modalOpen() {
        let modal = this._modalController()
        return !!(modal && modal.isOpen())
    }

    _openPreview(row) {
        if (!row.dataset.emailPreviewUrl) return
        let stack = this._stackController()
        if (stack) {
            this._select(row)
            stack.openFromRow(row)
            return
        }
        let modal = this._modalController()
        if (!modal) return
        this._modalRow = row
        this._select(row)
        modal.open(row.dataset.emailPreviewUrl, row)
    }

    _previewSibling(dir) {
        let emails = this._emailRows()
        let idx = emails.indexOf(this._modalRow)
        if (idx === -1) return
        let next = emails[idx + dir]
        if (!next) return
        this._advanceTo(next)
    }

    // Swap the modal to another row's preview without dropping triage mode
    // (open() closes first, so guard the intermediate "closed" event).
    _advanceTo(row) {
        let modal = this._modalController()
        if (!modal) return
        this._advancing = true
        this._modalRow = row
        this._select(row)
        modal.open(row.dataset.emailPreviewUrl, row)
    }

    _closeModal() {
        let modal = this._modalController()
        if (modal) modal.close()
    }

    // ----- Actions -----

    _archive(row, opts = {}) {
        if (row.dataset.triageBusy) return
        row.dataset.triageBusy = "1"

        let unit = this._unitFor(row)
        let emails = this._emailRows()
        let idx = emails.indexOf(row)
        let nextEmail = emails[idx + 1] || emails[idx - 1] || null

        // Advance the highlight BEFORE marking the row as leaving: _rows()
        // filters out .is-leaving rows, so resolving the neighbor afterwards
        // couldn't find the acted row's index and fell back to the top row.
        if (this._selected === row) this._advanceSelectionFrom(row)
        this._animateOut(unit, opts.fromSwipe)

        if (opts.advance) {
            if (nextEmail) {
                this._advanceTo(nextEmail)
            } else {
                this._closeModal()
            }
        }

        fetch(row.dataset.archiveUrl, {
            method: "POST",
            headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(() => this._removeUnit(unit))
            .catch(() => {
                delete row.dataset.triageBusy
                this._restoreUnit(unit, row)
            })
    }

    _draft(row, opts = {}) {
        let id = row.dataset.messageId
        if (!id) return

        this._setBadge(row, "Drafting…", "is-drafting")

        if (opts.advance) this._previewSibling(1)

        fetch(row.dataset.draftUrl, {
            method: "POST",
            headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((data) => {
                if (data.status === "ready") {
                    this._setBadge(row, "Draft ready", "is-ready")
                } else {
                    this._pendingDrafts.set(id, Date.now())
                    this._schedulePoll()
                }
            })
            .catch(() => this._setBadge(row, "Draft failed", "is-failed"))
    }

    // ----- Row animation helpers -----

    _unitFor(row) {
        return row.closest(".msg-swipe") || row
    }

    _animateOut(unit, alreadyMoved = false) {
        unit.classList.add("is-leaving")
        let row = unit.classList.contains("assistant-thread-item") ? unit : unit.querySelector(".assistant-thread-item")
        if (row && !alreadyMoved) row.style.transform = ""
    }

    _removeUnit(unit) {
        unit.style.height = `${unit.offsetHeight}px`
        // Force layout so the height transition to 0 actually animates.
        void unit.offsetHeight
        unit.classList.add("is-collapsed")
        unit.style.height = "0px"
        setTimeout(() => unit.remove(), 240)
    }

    _restoreUnit(unit, row) {
        unit.classList.remove("is-leaving", "is-collapsed")
        unit.style.height = ""
        row.style.transform = ""
        row.classList.add("is-action-failed")
        setTimeout(() => row.classList.remove("is-action-failed"), 1200)
    }

    // After acting on a row, move the highlight to the next row down (or the
    // previous one when the last row was acted on) so keyboard triage flows
    // through the list. Must run while the acted row is still in _rows()
    // (i.e. before it's marked .is-leaving). If there's no neighbor, keep the
    // selection only when the row remains in the list (draft on the only row).
    _advanceSelectionFrom(row) {
        let rows = this._rows()
        let idx = rows.indexOf(row)
        let next = (idx === -1 ? null : rows[idx + 1] || rows[idx - 1]) || null
        if (next && next !== row) {
            this._select(next)
        } else if (idx === -1) {
            this._selected = null
        }
    }

    // ----- Draft badge + polling -----

    _setBadge(row, text, state) {
        let badge = row.querySelector(".msg-row-badge")
        if (!badge) {
            badge = document.createElement("span")
            badge.className = "msg-row-badge"
            // The time now sits inside the stacked top line, so insert the
            // badge next to it within that line rather than under the row.
            let time = row.querySelector(".msg-row-time")
            if (time) {
                time.parentNode.insertBefore(badge, time)
            } else {
                row.appendChild(badge)
            }
        }
        badge.textContent = text
        badge.classList.remove("is-drafting", "is-ready", "is-failed")
        badge.classList.add(state)
    }

    _schedulePoll() {
        if (this._pollTimer || !this._pendingDrafts.size) return
        this._pollTimer = setTimeout(() => {
            this._pollTimer = null
            this._pollDraftStatus()
        }, this.DRAFT_POLL_MS)
    }

    _pollDraftStatus() {
        let ids = Array.from(this._pendingDrafts.keys())
        if (!ids.length) return

        let params = ids.map((id) => `ids[]=${encodeURIComponent(id)}`).join("&")
        fetch(`${this.draftStatusUrlValue}?${params}`, {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((data) => {
                let ready = (data.ready || []).map(String)
                ready.forEach((id) => {
                    this._pendingDrafts.delete(id)
                    let row = this._rowFor(id)
                    if (row) this._setBadge(row, "Draft ready", "is-ready")
                })
                this._expireStaleDrafts()
                this._schedulePoll()
            })
            .catch(() => this._schedulePoll())
    }

    _expireStaleDrafts() {
        let now = Date.now()
        this._pendingDrafts.forEach((startedAt, id) => {
            if (now - startedAt < this.DRAFT_TIMEOUT_MS) return
            this._pendingDrafts.delete(id)
            let row = this._rowFor(id)
            if (row) this._setBadge(row, "Draft failed", "is-failed")
        })
    }

    _rowFor(messageId) {
        return this.element.querySelector(`.assistant-thread-item[data-message-id="${messageId}"]`)
    }

    // ----- Touch swipe (inside the preview modal) -----
    //
    // Tinder-style: the card follows the finger (translate + slight rotation
    // + opacity falloff), flies off-screen past the threshold or on a flick,
    // then the archive/draft action fires. Below threshold it springs back.
    // Vertical drags are never claimed so the email content still scrolls.

    _bindModalSwipe() {
        let overlay = document.querySelector(".email-modal-overlay")
        if (!overlay || overlay.dataset.triageSwipeBound) return
        overlay.dataset.triageSwipeBound = "1"

        overlay.addEventListener("touchstart", (e) => this._modalTouchStart(e, overlay), { passive: true })
        overlay.addEventListener("touchmove", (e) => this._modalTouchMove(e), { passive: false })
        overlay.addEventListener("touchend", () => this._modalTouchEnd())
        overlay.addEventListener("touchcancel", () => this._modalTouchEnd({ cancel: true }))
    }

    _modalTouchStart(e, overlay) {
        if (e.touches.length !== 1 || !this._modalRow) return
        let card = overlay.querySelector(".email-modal")
        if (!card || card.dataset.triageFlying) return
        if (e.target.closest(".email-modal-close")) return
        this._modalSwipe = {
            card: card,
            startX: e.touches[0].clientX,
            startY: e.touches[0].clientY,
            width: card.offsetWidth,
            claimed: false,
            dx: 0,
            lastX: e.touches[0].clientX,
            lastT: e.timeStamp,
            velocity: 0,
            raf: null
        }
    }

    _modalTouchMove(e) {
        let s = this._modalSwipe
        if (!s) return
        let x = e.touches[0].clientX
        let dx = x - s.startX
        let dy = e.touches[0].clientY - s.startY

        if (!s.claimed) {
            // Axis lock: a clearly vertical drag belongs to the scrollable
            // email content, so hand the gesture back to the browser.
            if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) {
                this._modalSwipe = null
                return
            }
            if (Math.abs(dx) < this.SWIPE_CLAIM_PX || Math.abs(dx) < Math.abs(dy) * 1.2) return
            s.claimed = true
            s.card.classList.add("is-dragging")
            this._ensureModalBadges(s.card)
        }

        e.preventDefault()
        if (e.timeStamp > s.lastT) s.velocity = (x - s.lastX) / (e.timeStamp - s.lastT)
        s.lastX = x
        s.lastT = e.timeStamp
        s.dx = dx
        if (!s.raf) {
            s.raf = requestAnimationFrame(() => {
                s.raf = null
                this._renderModalDrag(s)
            })
        }
    }

    _renderModalDrag(s) {
        let dx = s.dx
        let progress = Math.min(Math.abs(dx) / (s.width * this.MODAL_THRESHOLD_RATIO), 1)
        s.card.style.transform = `translateX(${dx}px) rotate(${(dx * this.MODAL_ROTATE_DEG_PER_PX).toFixed(2)}deg)`
        s.card.style.opacity = `${1 - 0.25 * progress}`
        let archive = s.card.querySelector(".email-modal-swipe-badge--archive")
        let draft = s.card.querySelector(".email-modal-swipe-badge--draft")
        if (archive) archive.style.opacity = dx > 0 ? progress : 0
        if (draft) draft.style.opacity = dx < 0 ? progress : 0
    }

    _modalTouchEnd(opts = {}) {
        let s = this._modalSwipe
        this._modalSwipe = null
        if (!s || !s.claimed) return
        if (s.raf) cancelAnimationFrame(s.raf)
        this._suppressOverlayClick(s.card.closest(".email-modal-overlay"))

        let pastThreshold = Math.abs(s.dx) >= s.width * this.MODAL_THRESHOLD_RATIO
        let flick = Math.abs(s.velocity) >= this.MODAL_FLICK_VELOCITY && (s.velocity > 0) === (s.dx > 0)
        if (!opts.cancel && (pastThreshold || flick)) {
            this._flyOffModal(s, s.dx > 0 ? 1 : -1)
        } else {
            this._springBackModal(s)
        }
    }

    _springBackModal(s) {
        s.card.style.transition = "transform 0.2s ease, opacity 0.2s ease"
        s.card.style.transform = ""
        s.card.style.opacity = ""
        s.card.querySelectorAll(".email-modal-swipe-badge").forEach((b) => {
            b.style.transition = "opacity 0.2s ease"
            b.style.opacity = "0"
        })
        setTimeout(() => {
            s.card.style.transition = ""
            s.card.classList.remove("is-dragging")
            s.card.querySelectorAll(".email-modal-swipe-badge").forEach((b) => (b.style.transition = ""))
        }, 220)
    }

    _flyOffModal(s, dir) {
        nativeHaptic("medium")
        let row = this._modalRow
        s.card.dataset.triageFlying = "1"
        let offX = (window.innerWidth + s.width) * dir
        let rotation = 24 * dir
        s.card.style.transition = "transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.28s ease-out"
        s.card.style.transform = `translateX(${offX}px) rotate(${rotation}deg)`
        s.card.style.opacity = "0"
        // Fire the action once the card is gone; _archive/_draft advance the
        // modal to the next email (fresh overlay) or close it.
        setTimeout(() => {
            if (!row) return
            if (dir > 0) {
                this._archive(row, { advance: true })
            } else {
                this._draft(row, { advance: true })
            }
        }, 280)
    }

    // A completed drag must not double as a backdrop tap (which closes the
    // modal) or a click inside the card.
    _suppressOverlayClick(overlay) {
        if (!overlay) return
        let swallow = (e) => {
            e.stopPropagation()
            e.preventDefault()
        }
        overlay.addEventListener("click", swallow, { capture: true, once: true })
        setTimeout(() => overlay.removeEventListener("click", swallow, { capture: true }), 400)
    }

    // ----- Bottom action bar (Tinder-style buttons in the preview modal) -----
    //
    // Only rendered when the modal was opened from a triage row (archive +
    // draft are the only actions reachable here). Taps replay the same
    // fly-off animation and action as the matching swipe direction.

    _ensureActionBar() {
        let overlay = document.querySelector(".email-modal-overlay")
        if (!overlay) return
        let card = overlay.querySelector(".email-modal")
        if (!card || card.querySelector(".email-modal-action-bar")) return

        card.classList.add("has-action-bar")
        let bar = document.createElement("div")
        bar.className = "email-modal-action-bar"
        bar.innerHTML = `
            <button type="button" class="email-modal-action-btn email-modal-action-btn--draft" aria-label="Draft reply" title="Draft reply">
                <i class="bi bi-pencil-fill"></i>
            </button>
            <button type="button" class="email-modal-action-btn email-modal-action-btn--archive" aria-label="Archive" title="Archive">
                <i class="bi bi-check-lg"></i>
            </button>`
        bar.querySelector(".email-modal-action-btn--draft").addEventListener("click", (e) => {
            e.stopPropagation()
            this._actionBarTrigger(-1)
        })
        bar.querySelector(".email-modal-action-btn--archive").addEventListener("click", (e) => {
            e.stopPropagation()
            this._actionBarTrigger(1)
        })
        card.appendChild(bar)
    }

    _actionBarTrigger(dir) {
        if (!this._modalRow) return
        let overlay = document.querySelector(".email-modal-overlay")
        let card = overlay && overlay.querySelector(".email-modal")
        if (!card || card.dataset.triageFlying) return

        this._ensureModalBadges(card)
        let badge = card.querySelector(dir > 0 ? ".email-modal-swipe-badge--archive" : ".email-modal-swipe-badge--draft")
        if (badge) {
            badge.style.transition = "opacity 0.15s ease"
            badge.style.opacity = "1"
        }
        this._flyOffModal({ card: card, width: card.offsetWidth }, dir)
    }

    _ensureModalBadges(card) {
        if (card.querySelector(".email-modal-swipe-badge")) return
        let archive = document.createElement("span")
        archive.className = "email-modal-swipe-badge email-modal-swipe-badge--archive"
        archive.innerHTML = '<i class="bi bi-archive"></i> Archive'
        let draft = document.createElement("span")
        draft.className = "email-modal-swipe-badge email-modal-swipe-badge--draft"
        draft.innerHTML = '<i class="bi bi-pencil"></i> Draft'
        card.append(archive, draft)
    }

}
